import { z } from 'zod'
import { decimalSchema, timestampSchema, uuidSchema } from './common.ts'
import { exchangeRateUnitsSchema, exchangeRateValueSchema, isoCurrencySchema } from './warehouse.ts'

export const commercialOperationTypeSchema = z.enum(['domestic', 'export'])
export type CommercialOperationType = z.infer<typeof commercialOperationTypeSchema>

export const commercialOperationTypeLabels: Record<CommercialOperationType, string> = {
  domestic: 'Внутрішня операція',
  export: 'Експорт',
}

export const pricingCurrencySchema = z.object({
  code: isoCurrencySchema,
  name: z.string().trim().min(1).max(100),
  isActive: z.boolean(),
  isDefault: z.boolean(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
})
export type PricingCurrency = z.infer<typeof pricingCurrencySchema>

export const createPricingCurrencyInputSchema = z.strictObject({
  code: isoCurrencySchema,
  name: z.string().trim().min(1).max(100),
  isDefault: z.boolean().default(false),
})
export type CreatePricingCurrencyInput = z.infer<typeof createPricingCurrencyInputSchema>

export const updatePricingCurrencyInputSchema = z
  .strictObject({
    name: z.string().trim().min(1).max(100).optional(),
    isActive: z.boolean().optional(),
    isDefault: z.boolean().optional(),
  })
  .refine((input) => Object.keys(input).length > 0, 'At least one field must be updated')
export type UpdatePricingCurrencyInput = z.infer<typeof updatePricingCurrencyInputSchema>

export const vatRateSchema = z
  .string()
  .regex(/^\d+(\.\d{1,4})?$/, 'Вкажіть ставку у форматі 20 або 7.5')
  .refine((value) => Number(value) <= 100, 'Ставка має бути від 0 до 100')

export const vatTreatmentSchema = z.object({
  id: uuidSchema,
  code: z.string(),
  name: z.string(),
  rate: decimalSchema,
  pricesIncludeVat: z.boolean(),
  isActive: z.boolean(),
  isDefault: z.boolean(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
})
export type VatTreatment = z.infer<typeof vatTreatmentSchema>

export const createVatTreatmentInputSchema = z.strictObject({
  code: z.string().trim().min(1).max(50),
  name: z.string().trim().min(1).max(100),
  rate: vatRateSchema,
  pricesIncludeVat: z.boolean(),
  isDefault: z.boolean().default(false),
})
export type CreateVatTreatmentInput = z.infer<typeof createVatTreatmentInputSchema>

export const updateVatTreatmentInputSchema = z
  .strictObject({
    name: z.string().trim().min(1).max(100).optional(),
    rate: vatRateSchema.optional(),
    pricesIncludeVat: z.boolean().optional(),
    isActive: z.boolean().optional(),
    isDefault: z.boolean().optional(),
  })
  .refine((input) => Object.keys(input).length > 0, 'At least one field must be updated')
export type UpdateVatTreatmentInput = z.infer<typeof updateVatTreatmentInputSchema>

export const commercialManualExchangeRateInputSchema = z.object({
  effectiveRateDate: z.iso.date(),
  rate: exchangeRateValueSchema,
  units: exchangeRateUnitsSchema,
  sourceUrl: z.string().url(),
  reason: z.string().trim().min(3).max(500),
})

export type CommercialManualExchangeRateInput = z.infer<
  typeof commercialManualExchangeRateInputSchema
>

export const dealPricingInputSchema = z
  .strictObject({
    currencyCode: isoCurrencySchema,
    vatTreatmentId: uuidSchema.nullable(),
    operationType: commercialOperationTypeSchema,
    counterpartyCountryCode: z
      .string()
      .trim()
      .toUpperCase()
      .regex(/^[A-Z]{2}$/)
      .nullable(),
    rateDate: z.iso.date(),
  })
  .superRefine((input, context) => {
    if (input.operationType === 'export' && !input.counterpartyCountryCode) {
      context.addIssue({
        code: 'custom',
        path: ['counterpartyCountryCode'],
        message: 'Для експорту вкажіть країну контрагента',
      })
    }
  })

export type DealPricingInput = z.infer<typeof dealPricingInputSchema>
